import { useQuery } from '@tanstack/react-query'
import { apiService } from '../../../shared/services/api'
import { userAccountApi } from '../../../shared/services/userAccountApi'
import type { AnalyticsSeriesPoint } from '../../../shared/types/analytics'

/**
 * 링크 상세 패널이 쓰는 조회.
 * 패널을 열 때만 부르도록 코드가 없으면 조회하지 않는다.
 */

/** 패널 그래프에 필요한 만큼만 추린 형태 */
export interface LinkWeeklyClicks {
  totalClicks: number
  weekClicks: number
  points: AnalyticsSeriesPoint[]
}

/** 비밀번호·만료일처럼 플랜에 묶인 기능의 해금 여부 */
export interface LinkFeatures {
  plan: string
  passwordProtection: boolean
  expirationDates: boolean
}

const WEEKLY_STALE_MS = 60_000
const FEATURES_STALE_MS = 5 * 60_000

export const LINK_WEEKLY_KEY = ['link-weekly-clicks'] as const
export const LINK_FEATURES_KEY = ['link-features'] as const

/** 최근 7일 클릭 추이. shortCode 가 null 이면 조회하지 않는다. */
export function useLinkWeeklyClicks(shortCode: string | null) {
  return useQuery<LinkWeeklyClicks>({
    queryKey: [...LINK_WEEKLY_KEY, shortCode],
    enabled: Boolean(shortCode),
    staleTime: WEEKLY_STALE_MS,
    queryFn: async () => {
      const analytics = await apiService.getLinkAnalytics(shortCode as string, {
        range: '7d',
      })
      return {
        totalClicks: analytics.summary?.totalClicksAllTime ?? 0,
        weekClicks: analytics.summary?.clicks ?? 0,
        points: analytics.timeSeries ?? [],
      }
    },
  })
}

/** 플랜 기능. 응답을 읽지 못하면 잠긴 것으로 본다 (잘못 열어 주지 않는다). */
export function useLinkFeatures() {
  return useQuery<LinkFeatures>({
    queryKey: LINK_FEATURES_KEY,
    staleTime: FEATURES_STALE_MS,
    queryFn: async () => {
      const subscription = await userAccountApi.getSubscription<{
        currentPlan?: { type?: string }
        features?: { passwordProtection?: boolean; expirationDates?: boolean }
      }>()
      return {
        plan: subscription?.currentPlan?.type || 'free',
        passwordProtection: subscription?.features?.passwordProtection ?? false,
        expirationDates: subscription?.features?.expirationDates ?? false,
      }
    },
  })
}
